import { useMemo } from 'react';
import { combineQueryResults } from '../../../../CommonUtils/combineQueryResults';
import { AsyncComponentHookResult } from '../../../../Types/ComponentHookResult';
import { GoalTypeDTO } from '../../../types/goalsDTO';
import { useMonteCarloQuery } from '../../api/useMonteCarloQuery';
import { RetirementTab } from '../../goals/Retirement/Retirement.types';
import { getGoalExplanations } from '../../helpers/getGoalExplanations';
import { useGoal } from '../../hooks/useGoal';

interface ExplanationData {
  title?: string;
  message?: string;
}

interface UseExplanationProps {
  selectedTab: string;
  goalId?: string;
}

export const useExplanations = ({
  selectedTab,
  goalId,
}: UseExplanationProps): AsyncComponentHookResult<ExplanationData> => {
  const useGoalQueryResult = useGoal({ goalId });
  const monteCarloQueryResult = useMonteCarloQuery({ enabled: false });

  const data = useMemo(() => {
    if (!useGoalQueryResult.data) {
      return null;
    }

    const goal = useGoalQueryResult.data;

    const explanations = getGoalExplanations(goal);

    if (!explanations) {
      return null;
    }

    const selectedTabExplanations = explanations.find(({ Section }) => Section === selectedTab);

    switch (goal.GoalType) {
      case GoalTypeDTO.Retirement: {
        if (selectedTab === RetirementTab.Assumptions) {
          return {
            title: 'Below are the assumptions made for this plan',
          };
        }

        if (selectedTab === RetirementTab.MonteCarlo) {
          if (!monteCarloQueryResult.data) {
            monteCarloQueryResult.refetch();
            return null;
          }

          const { GoalExplanationsTitle, GoalExplanationsMessage } = monteCarloQueryResult.data;

          return {
            title: GoalExplanationsTitle,
            message: GoalExplanationsMessage,
          };
        }

        if (!selectedTabExplanations) {
          return null;
        }

        return {
          title: selectedTabExplanations.Title,
          message: selectedTabExplanations.Message,
        };
      }

      default: {
        return {
          title: selectedTabExplanations?.Title,
          message: selectedTabExplanations?.Message,
        };
      }
    }
  }, [useGoalQueryResult.data, selectedTab, monteCarloQueryResult]);

  return combineQueryResults([{ ...useGoalQueryResult, data }, monteCarloQueryResult]);
};
